import React, {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useState,
    type ReactNode,
} from 'react';
import { Check, Copy, type LucideIcon } from 'lucide-react';
import { AppDialog, type AppDialogVariant } from '@/components/ui/AppDialog';

export interface ConfirmDialogOptions {
    title: string;
    message: ReactNode;
    confirmLabel?: string;
    cancelLabel?: string;
    variant?: AppDialogVariant;
    icon?: LucideIcon;
}

export interface AlertDialogOptions {
    title?: string;
    message: ReactNode;
    okLabel?: string;
    variant?: AppDialogVariant;
    icon?: LucideIcon;
}

export interface CopyableDialogOptions {
    title: string;
    message?: ReactNode;
    value: string;
    okLabel?: string;
}

interface DialogContextValue {
    confirm: (options: ConfirmDialogOptions) => Promise<boolean>;
    alert: (options: AlertDialogOptions | string) => Promise<void>;
    showCopyable: (options: CopyableDialogOptions) => Promise<void>;
}

type ActiveDialog =
    | {
          kind: 'confirm';
          options: ConfirmDialogOptions;
          resolve: (value: boolean) => void;
      }
    | {
          kind: 'alert';
          options: AlertDialogOptions;
          resolve: () => void;
      }
    | {
          kind: 'copyable';
          options: CopyableDialogOptions;
          resolve: () => void;
      };

const DialogContext = createContext<DialogContextValue | null>(null);

function normalizeAlertOptions(options: AlertDialogOptions | string): AlertDialogOptions {
    if (typeof options === 'string') {
        return { message: options, title: 'Notice', variant: 'info' };
    }
    return {
        title: options.title ?? 'Notice',
        message: options.message,
        okLabel: options.okLabel,
        variant: options.variant ?? 'info',
        icon: options.icon,
    };
}

export const DialogProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [active, setActive] = useState<ActiveDialog | null>(null);
    const [copied, setCopied] = useState(false);

    const close = useCallback(() => {
        setActive((current) => {
            if (!current) return null;
            if (current.kind === 'confirm') current.resolve(false);
            else current.resolve();
            return null;
        });
        setCopied(false);
    }, []);

    const confirm = useCallback((options: ConfirmDialogOptions) => {
        return new Promise<boolean>((resolve) => {
            setActive({ kind: 'confirm', options, resolve });
        });
    }, []);

    const alert = useCallback((options: AlertDialogOptions | string) => {
        const normalized = normalizeAlertOptions(options);
        return new Promise<void>((resolve) => {
            setActive({ kind: 'alert', options: normalized, resolve });
        });
    }, []);

    const showCopyable = useCallback((options: CopyableDialogOptions) => {
        return new Promise<void>((resolve) => {
            setCopied(false);
            setActive({ kind: 'copyable', options, resolve });
        });
    }, []);

    const value = useMemo(
        () => ({ confirm, alert, showCopyable }),
        [confirm, alert, showCopyable],
    );

    const finishConfirm = (result: boolean) => {
        if (active?.kind !== 'confirm') return;
        active.resolve(result);
        setActive(null);
        setCopied(false);
    };

    const finishDismiss = () => {
        if (!active || active.kind === 'confirm') {
            close();
            return;
        }
        active.resolve();
        setActive(null);
        setCopied(false);
    };

    const handleCopy = async () => {
        if (active?.kind !== 'copyable') return;
        try {
            await navigator.clipboard.writeText(active.options.value);
            setCopied(true);
        } catch {
            // Selection in the input still lets the user copy manually.
        }
    };

    return (
        <DialogContext.Provider value={value}>
            {children}
            {active?.kind === 'confirm' && (
                <AppDialog
                    open
                    title={active.options.title}
                    message={active.options.message}
                    variant={active.options.variant ?? 'default'}
                    icon={active.options.icon}
                    onClose={() => finishConfirm(false)}
                    actions={[
                        {
                            label: active.options.cancelLabel ?? 'Cancel',
                            onClick: () => finishConfirm(false),
                            variant: 'secondary',
                        },
                        {
                            label: active.options.confirmLabel ?? 'Confirm',
                            onClick: () => finishConfirm(true),
                            variant: active.options.variant === 'destructive' ? 'destructive' : 'primary',
                            autoFocus: true,
                        },
                    ]}
                />
            )}
            {active?.kind === 'alert' && (
                <AppDialog
                    open
                    title={active.options.title ?? 'Notice'}
                    message={active.options.message}
                    variant={active.options.variant ?? 'info'}
                    icon={active.options.icon}
                    onClose={finishDismiss}
                    actions={[
                        {
                            label: active.options.okLabel ?? 'OK',
                            onClick: finishDismiss,
                            variant: 'primary',
                            autoFocus: true,
                        },
                    ]}
                />
            )}
            {active?.kind === 'copyable' && (
                <AppDialog
                    open
                    title={active.options.title}
                    message={active.options.message ?? 'Copy the value below.'}
                    variant="info"
                    onClose={finishDismiss}
                    actions={[
                        {
                            label: copied ? 'Copied' : 'Copy',
                            onClick: () => { void handleCopy(); },
                            variant: 'secondary',
                        },
                        {
                            label: active.options.okLabel ?? 'Done',
                            onClick: finishDismiss,
                            variant: 'primary',
                            autoFocus: true,
                        },
                    ]}
                >
                    <div className="space-y-2">
                        <input
                            readOnly
                            value={active.options.value}
                            onFocus={(e) => e.currentTarget.select()}
                            className="w-full bg-bg-primary/40 border border-divider-subtle rounded-xl px-3 py-2 text-xs font-mono text-text-primary break-all"
                        />
                        <p className="text-xs text-text-muted flex items-center gap-1">
                            {copied ? <Check size={12} className="text-green-400" /> : <Copy size={12} />}
                            {copied ? 'Copied to clipboard' : 'Select the field or use Copy if the browser blocked clipboard access.'}
                        </p>
                    </div>
                </AppDialog>
            )}
        </DialogContext.Provider>
    );
};

export function useDialog(): DialogContextValue {
    const ctx = useContext(DialogContext);
    if (!ctx) {
        throw new Error('useDialog must be used within DialogProvider');
    }
    return ctx;
}
